import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { PaDrawer, PaStatusBadge } from "components/platform-admin";
import {
  ACCOUNT_ISSUE_LABELS,
  ACCOUNT_ISSUE_VARIANTS,
  analyzeUserAccount,
  getSelfServiceEmailGuidance,
  getUserSupportPlaybooks,
} from "./userSupport.utils";
import { formatUserTypeLabel } from "./userSearch.utils";

const UserSupportDrawer = ({
  open,
  onClose,
  user,
  permissions = {},
  onRunAction,
}) => {
  if (!user) {
    return null;
  }

  const account = analyzeUserAccount(user);
  const playbooks = getUserSupportPlaybooks(user, permissions);
  const emailGuidance = getSelfServiceEmailGuidance(user);
  const lastLogin = user.last_login_at
    ? moment(user.last_login_at).format("D MMM YYYY, h:mm a")
    : "Never";

  return (
    <PaDrawer
      open={open}
      onClose={onClose}
      title="Account support"
      description={`Guided fixes for ${user.email}`}
      widthClassName="max-w-xl"
    >
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">{user.name || "—"}</p>
              <p className="text-sm text-gray-600">{user.email}</p>
              <p className="mt-1 text-xs text-gray-500">
                {formatUserTypeLabel(user.user_type)} · Last login: {lastLogin}
              </p>
            </div>
            <PaStatusBadge
              label={user.status ? "Active" : "Locked"}
              variant={user.status ? "success" : "danger"}
            />
          </div>

          {account.issues.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {account.issues.map((issue) => (
                <PaStatusBadge
                  key={issue}
                  label={ACCOUNT_ISSUE_LABELS[issue] || issue}
                  variant={ACCOUNT_ISSUE_VARIANTS[issue] || "neutral"}
                />
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-gray-600">No account issues detected.</p>
          )}
        </section>

        <section className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-4">
          <h3 className="text-sm font-semibold text-gray-900">{emailGuidance.title}</h3>
          <p className="mt-1 text-sm text-gray-600">
            Prefer self-service when the user can sign in — fewer support touchpoints.
          </p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-600">
            {emailGuidance.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-gray-900">Support playbooks</h3>
          <p className="mt-1 text-sm text-gray-500">
            Common support requests mapped to the actions below.
          </p>

          <ul className="mt-4 space-y-4">
            {playbooks.map((playbook) => (
              <li
                key={playbook.id}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{playbook.title}</p>
                    <p className="mt-1 text-sm text-gray-600">{playbook.description}</p>
                  </div>
                  {playbook.primaryAction ? (
                    <button
                      type="button"
                      onClick={() => onRunAction(playbook.primaryAction.id, user, playbook)}
                      className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      {playbook.primaryAction.label}
                    </button>
                  ) : null}
                </div>

                {playbook.steps?.length ? (
                  <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-gray-600">
                    {playbook.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                ) : null}

                {playbook.id === "no_tenant_access" ? (
                  <Link
                    to="/tenants"
                    className="mt-3 inline-flex text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Browse tenants to assign access
                  </Link>
                ) : null}

                {playbook.tenantLink ? (
                  <Link
                    to={`/tenants/${playbook.tenantLink}`}
                    className="mt-3 inline-flex text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Open suspended tenant
                  </Link>
                ) : null}

                {playbook.tenantSupportLink ? (
                  <Link
                    to={playbook.tenantSupportLink}
                    className="mt-3 inline-flex text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Open tenant support guides
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-gray-900">Individual actions</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {permissions.lock ? (
              <button
                type="button"
                onClick={() =>
                  onRunAction(user.status ? "lock" : "unlock", user)
                }
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {user.status ? "Lock account" : "Unlock account"}
              </button>
            ) : null}
            {permissions.revoke ? (
              <button
                type="button"
                onClick={() => onRunAction("revoke", user)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Revoke sessions
              </button>
            ) : null}
            {permissions.mfa ? (
              <button
                type="button"
                onClick={() => onRunAction("mfa", user)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Reset MFA
              </button>
            ) : null}
            {permissions.passwordReset ? (
              <button
                type="button"
                onClick={() => onRunAction("reset_password", user)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Set password
              </button>
            ) : null}
            {permissions.emailUpdate ? (
              <button
                type="button"
                onClick={() => onRunAction("change_email", user)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Change email
              </button>
            ) : null}
            {permissions.ghost ? (
              <button
                type="button"
                onClick={() => onRunAction("ghost", user)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Ghost as user
              </button>
            ) : null}
          </div>
        </section>
      </div>
    </PaDrawer>
  );
};

export default UserSupportDrawer;
